/*
* Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
  distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
  prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
  content, please contact the author for proper authorization and agreement.
  */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnFiltersState, SortingState, VisibilityState } from '@tanstack/react-table'
import { CompanyContact } from 'types/entities/CompanyContact'
import { ContactCompany } from 'types/entities/ContactCompany'
import { Lead } from 'types/entities/Lead'
import { Offer } from 'types/entities/Offer'
import { ContactsFilterType } from 'types/enums/ContactsFilterType'

type TableSearchScalar = string | number | boolean | null
type TableSearch =
    | TableSearchScalar
    | TableSearchScalar[]
    | Record<string, TableSearchScalar | TableSearchScalar[]>

interface ContactsTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    view: ContactsFilterType | 'all'
    columnVisibility?: VisibilityState
}

interface ContactsTableMetaState {
    currentPage: number
    perPage: number
    total: number
    sortBy: string | null
    sort: 'asc' | 'desc' | null
    search: TableSearch
}

interface ContactsTableState {
    ui: ContactsTableUiState
    meta: ContactsTableMetaState | null
    hasLoaded: boolean
}

export interface ContactsState {
    all: CompanyContact[]
    selectedContact: CompanyContact | null
    selectedContactCompanies: ContactCompany[]
    selectedContactLeads: Lead[]
    selectedContactOffers: Offer[]
    table: ContactsTableState
}
const initialState: ContactsState = {
    all: [],
    selectedContact: null,
    selectedContactCompanies: [],
    selectedContactLeads: [],
    selectedContactOffers: [],
    table: {
        ui: {
            pageIndex: 0,
            pageSize: 50,
            globalFilter: '',
            columnFilters: [],
            sorting: [{ id: 'id', desc: true }],
            view: 'all',
        },
        meta: null,
        hasLoaded: false,
    },
}

const contactsSlice = createSlice({
    name: 'contacts',
    initialState,
    reducers: {
        setContacts(state, action: PayloadAction<CompanyContact[]>) {
            state.all = action.payload ?? []
        },
        updateContact(state, action: PayloadAction<CompanyContact>) {
            const contact = action.payload
            const index = state.all.findIndex((row) => row.id === contact.id)
            if (index !== -1) state.all[index] = {...state.all[index], ...contact}
            if (state.selectedContact?.id === contact.id) {
                state.selectedContact = {...state.selectedContact, ...contact}
            }
        },
        setSelectedContact(
            state,
            action: PayloadAction<CompanyContact | null>
        ) {
            state.selectedContact = action.payload
        },
        setSelectedContactCompanies(
            state,
            action: PayloadAction<ContactCompany[]>
        ) {
            state.selectedContactCompanies = action.payload ?? []
        },
        setSelectedContactLeads(state, action: PayloadAction<Lead[]>) {
            state.selectedContactLeads = action.payload ?? []
        },
        setSelectedContactOffers(state, action: PayloadAction<Offer[]>) {
            state.selectedContactOffers = action.payload ?? []
        },
        pushContactCompany(state, action: PayloadAction<ContactCompany>) {
            state.selectedContactCompanies.push(action.payload)
        },
        removeContactCompany(
            state,
            action: PayloadAction<{ companyId: number }>
        ) {
            state.selectedContactCompanies =
                state.selectedContactCompanies.filter(
                    (c) => c.company_id !== action.payload.companyId
                )
        },
        setContactsTableUiState(
            state,
            action: PayloadAction<Partial<ContactsTableUiState>>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            if (!state.table.ui) {
                state.table.ui = { ...initialState.table.ui }
            }
            state.table.ui = {
                ...state.table.ui,
                ...action.payload,
            }
        },
        setContactsTableMeta(
            state,
            action: PayloadAction<ContactsTableMetaState>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            state.table.meta = action.payload
            state.table.hasLoaded = true
        },
        resetState: () => initialState,
    },
})

export const {
    updateContact,
    setContacts,
    setSelectedContact,
    setSelectedContactCompanies,
    setSelectedContactLeads,
    setSelectedContactOffers,
    pushContactCompany,
    removeContactCompany,
    setContactsTableUiState,
    setContactsTableMeta,
    resetState,
} = contactsSlice.actions

export default contactsSlice.reducer
